<template>
  <div v-if="totalPages > 0" class="mt-4 px-4 py-3 flex items-center justify-between sm:px-6">
    <div class="flex-1 flex justify-between sm:hidden">
      <button
        @click="!buttons[0].disabled ? $emit('page-change', {page: buttons[0].page}): ''"
        :disabled="buttons[0].disabled"
        :class="{ 'opacity-25 cursor-not-allowed': buttons[0].disabled }"
        class="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
        Previous
      </button>
      <button
        :disabled="buttons[this.pages.length - 1].disabled"
        @click="!buttons[this.pages.length - 1].disabled ? $emit('page-change', {page: buttons[this.pages.length - 1].page}) : ''"
        :class="{ 'opacity-25 cursor-not-allowed': buttons[this.pages.length - 1].disabled }"
        class="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
        Next
      </button>
    </div>
    <div class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
      <div>
        <p class="text-sm text-gray-700">
          Showing
          {{ ' ' }}
          <span class="font-medium">{{ start + 1 }}</span>
          {{ ' ' }}
          to
          {{ ' ' }}
          <span class="font-medium">{{ end }}</span>
          {{ ' ' }}
          of
          {{ ' ' }}
          <span class="font-medium">{{ totalItems }}</span>
          {{ ' ' }}
          results
        </p>
      </div>
      <div v-if="totalPages > 1">
        <nav class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">

          <template v-for="(button, key) in buttons" :key="key">
            <span v-if="button.page === '...'"
                  class="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
              ...
            </span>
            <button v-else-if="key === 0"
                    @click="!button.disabled ? $emit('page-change', {page: button.page}): ''"
                    :disabled="button.disabled"
                    :class="{ 'opacity-25 cursor-not-allowed': button.disabled }"
                    class="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50">
              <span class="sr-only">{{ button.title }}</span>
              <ChevronLeftIcon class="h-5 w-5" aria-hidden="true"/>
            </button>
            <button v-else-if="key === this.pages.length - 1"
                    :disabled="button.disabled"
                    @click="!button.disabled ? $emit('page-change', {page: button.page}) : ''"
                    :class="{ 'opacity-25 cursor-not-allowed': button.disabled }"
                    class="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50">
              <span class="sr-only">{{ button.title }}</span>
              <ChevronRightIcon class="h-5 w-5" aria-hidden="true"/>
            </button>
            <button v-else
                    @click="!button.active ? $emit('page-change', {page: button.page}) : ''"
                    :aria-current="button.active ? 'page' : ''"
                    :class="{ 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50': !button.active, 'z-10 bg-primary-50 border-primary-500 text-primary-600 ': button.active}"
                    class="relative inline-flex items-center px-4 py-2 border text-sm font-medium">
              <span v-html="button.html"/>
            </button>
          </template>


        </nav>
      </div>
    </div>
  </div>
</template>

<script>
import {ChevronLeftIcon, ChevronRightIcon} from '@heroicons/vue/solid'

export default {
  name: "Paginate",
  components: {
    ChevronLeftIcon,
    ChevronRightIcon,
  },
  props: {
    page: {
      type: Number,
      default: 0,
      validator: (page) => {
        return page >= 0;
      },
    },

    itemsPerPage: {
      type: Number,
      default: 10,
      validator: (itemsPerPage) => {
        return itemsPerPage > 0;
      },
    },

    maxVisiblePages: {
      type: Number,
      default: 5,
      validator: (maxVisiblePages) => {
        return maxVisiblePages > 0;
      },
    },

    totalItems: {
      type: Number,
      required: true,
      validator: (totalItems) => {
        return totalItems >= 0;
      },
    },

    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {}
  },


  methods: {
    pageChange(page) {
      if (page >= this.totalPages && page !== 0 && this.totalPages !== 0) {
        throw new Error('page may be maximum the total number of pages minus one');
      }

      this.rangeChange();
    },

    rangeChange() {
      if (this.page >= this.totalPages && this.totalPages !== 0) {
        this.$emit('page-change', this.totalPages - 1);
      } else {
        this.$emit('range-change', this.start, this.end);
      }
    },

    html(page, key) {
      if (key === 0) {
        return '<i class="fa fa-angle-left"></i>';
      }

      if (key === this.pages.length - 1) {
        return '<i class="fa fa-angle-right"></i>';
      }

      if (page === '...') {
        return page;
      }

      return (page + 1) + '';
    },

    disabled(page, key) {
      return key === 0 && this.page === 0 ||
        key === this.pages.length - 1 && this.page === this.totalPages - 1 ||
        page === '...';
    },

    title(key) {
      if (key === 0) {
        return 'previous';
      }

      if (key === this.pages.length - 1) {
        return 'next';
      }

      return '';
    },
  },


  computed: {
    start() {
      return this.page * this.itemsPerPage;
    },

    end() {
      let end = this.start + this.itemsPerPage;

      return this.totalItems < end ? this.totalItems : end;
    },

    totalPages() {
      if (this.itemsPerPage === 0) {
        return 0;
      }

      return Math.ceil(this.totalItems / this.itemsPerPage);
    },

    pages() {
      let filteredPages = this.filteredPages;
      let pages = filteredPages ?
        [
          filteredPages[0] - 1 === 1 ? 1 : '...',
          ...filteredPages,
          filteredPages[filteredPages.length - 1] + 1 === this.totalPages - 2 ? this.totalPages - 2 : '...',
        ] :
        [
          ...Array((this.totalPages >= 2 ? this.totalPages - 2 : 0)).keys(),
        ].map(page => page + 1);

      return [
        this.page - 1,
        0,
        ...pages,
        this.totalPages - 1,
        this.page + 1,
      ];
    },

    filteredPages() {
      let diff = this.maxVisiblePages / 2;
      let toFilterPages = [
        ...Array(this.totalPages).keys(),
      ].slice(2, -2);

      if (toFilterPages.length > this.maxVisiblePages) {
        let diffFirst = this.page - toFilterPages[0];
        let diffLast = this.page - toFilterPages[toFilterPages.length - 1];

        if (diffFirst < diff) {
          return toFilterPages.slice(0, this.maxVisiblePages);
        } else if (diffLast >= -diff) {
          return toFilterPages.slice(-this.maxVisiblePages);
        } else {
          return toFilterPages.filter(page => {
            let diffPage = this.page - page;

            return diffPage < 0 ? Math.abs(diffPage) <= diff : diffPage < diff;
          });
        }
      }

      return null;
    },

    buttons() {
      return this.pages.map((page, key) => {
        return {
          page,
          active: page === this.page,
          disabled: this.disabled(page, key),
          html: this.html(page, key),
          title: this.title(key),
          loading: this.loading && page === this.page,
        };
      });
    },
  },


  watch: {
    page: {
      handler: 'pageChange',
      immediate: true,
    },

    itemsPerPage: {
      handler: 'rangeChange',
    },

    totalItems: {
      handler: 'rangeChange',
    },
  },

}
</script>